import { getChatCompletion } from '@/lib/ai/chatCompletion';
import { createClient } from '@/lib/supabase/client';

export interface ApplicationScoreResult {
  experience_level: number;
  reliability_fit: number;
  recommended_tier: 'entry' | 'standard' | 'premium';
  ai_summary: string;
  recommended_action: string;
}

interface ApplicationData {
  name: string;
  experience: string;
  availability: string[];
  hoursPerWeek: string;
  hasTransportation: string;
  whyJoin: string;
  backgroundCheck: boolean;
}

export async function scoreApplicationWithClaude(
  application: ApplicationData
): Promise<ApplicationScoreResult | null> {
  try {
    const prompt = `You are an expert property management hiring analyst. Analyze this cleaner job application and return a JSON object with exactly these fields:

- experience_level (integer 1-10): How experienced is this applicant? 10 = highly experienced professional, 1 = no experience
  - "none" or "No formal experience" → 1-2
  - "less-1"or "Less than 1 year" → 3-4 -"1-2"or "1–2 years" → 5-6 -"2-5"or "2–5 years" → 7-8 -"5+" or "5+ years" → 9-10
- reliability_fit (integer 1-10): How reliable and suitable is this applicant for vacation rental cleaning? Consider availability breadth, transportation, hours, and motivation. 10 = ideal fit, 1 = poor fit
- recommended_tier (string): Must be exactly one of: "entry", "standard", or "premium"
  - entry = new/inexperienced cleaners needing training
  - standard = moderately experienced, reliable cleaners
  - premium = highly experienced, flexible, motivated cleaners
- ai_summary (string): One concise sentence (max 15 words) summarizing this applicant's key strength or concern
- recommended_action (string): One specific hiring action for the admin (max 10 words)

Applicant Details:
- Name: ${application.name}
- Experience Level: ${application.experience}
- Available Days: ${application.availability.join(', ') || 'Not specified'}
- Hours Per Week: ${application.hoursPerWeek || 'Not specified'}
- Has Transportation: ${application.hasTransportation || 'Not specified'}
- Background Check Consent: ${application.backgroundCheck ? 'Yes' : 'No'}
- Why They Want to Join: ${application.whyJoin || 'No message provided'}

Respond ONLY with valid JSON. No markdown, no explanation, just the JSON object.`;

    const response = await getChatCompletion(
      'ANTHROPIC',
      'claude-haiku-4-5',
      [
        { role: 'user', content: prompt }
      ],
      {
        temperature: 0.2,
        max_tokens: 300,
      }
    );

    const content = response?.choices?.[0]?.message?.content;
    if (!content) return null;

    // Strip any markdown code fences if present
    const cleaned = content.replace(/```json\n?|```\n?/g, '').trim();
    const parsed = JSON.parse(cleaned) as ApplicationScoreResult;

    // Validate required fields
    if (
      typeof parsed.experience_level !== 'number' ||
      typeof parsed.reliability_fit !== 'number' ||
      !['entry', 'standard', 'premium'].includes(parsed.recommended_tier) ||
      typeof parsed.ai_summary !== 'string' ||
      typeof parsed.recommended_action !== 'string'
    ) {
      console.warn('[CleanerScoring] Invalid score structure:', parsed);
      return null;
    }

    // Clamp scores to 1-10
    parsed.experience_level = Math.min(10, Math.max(1, Math.round(parsed.experience_level)));
    parsed.reliability_fit = Math.min(10, Math.max(1, Math.round(parsed.reliability_fit)));

    return parsed;
  } catch (err) {
    console.warn('[CleanerScoring] Claude scoring failed:', err);
    return null;
  }
}

export async function saveApplicationScore(
  applicationId: string,
  score: ApplicationScoreResult
): Promise<void> {
  try {
    const supabase = createClient();
    const { error } = await supabase.from('application_scores').insert({
      application_id: applicationId,
      experience_level: score.experience_level,
      reliability_fit: score.reliability_fit,
      recommended_tier: score.recommended_tier,
      ai_summary: score.ai_summary,
      recommended_action: score.recommended_action,
    });
    if (error) {
      console.warn('[CleanerScoring] Failed to save score:', error.message);
    }
  } catch (err) {
    console.warn('[CleanerScoring] Save score error:', err);
  }
}
